import { useCallback, useEffect, useRef, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import api from "../api"
import { ReactComponent as CalendarIcon } from "../assets/images/icons/ic-calendar.svg"
import { ReactComponent as MaterialIcon } from "../assets/images/icons/ic-files-inactive.svg"
import { ReactComponent as LessonIcon } from "../assets/images/icons/ic-lessons-inactive.svg"
import { ReactComponent as StudentsIcon } from "../assets/images/icons/ic-students.svg"
import { Each } from "../common/Each"
import Button from "../components/Button"
import MeatBallsMenu from "../components/MeatBallsMenu"
import MaterialButton from "../components/MaterialButton"
import Select from "../components/Select"
import Skeleton from "../components/Skeleton"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import Card from "../components/cards/Card"
import AlertDialog from "../components/dialogs/AlertDialog"
import Dialog from "../components/dialogs/Dialog"
import ExerciseDialog from "../components/dialogs/ExerciseDialog"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import { DialogStatus } from "../enums"
import { formatDateV2, formatTimeV2 } from "../utils"
import styles from "./Exercise.module.css"
import Back from "../components/Back"
import Markdown from "react-markdown"

const Exercise = () => {
  const { t } = useTranslation()
  const { exerciseId } = useParams()
  const navigate = useNavigate()

  const [exercise, setExercise] = useState(null)
  const [loading, setLoading] = useState(true)

  const [openEditDialog, setOpenEditDialog] = useState(false)
  const dialogStatus = DialogStatus.Default
  const [usersExercises, setUsersExercises] = useState([])
  const [filteredUsersExercises, setFilteredUsersExercises] = useState([])
  const [submissionsFilter, setSubmissionsFilter] = useState(0)
  const [studentSearchQuery, setStudentSearchQuery] = useState("")
  const [dialogTimeout, setDialogTimeout] = useState(null)

  const [openAlert, setOpenAlert] = useState(false)
  const [alertStatus, setAlertStatus] = useState(DialogStatus.Default)
  const [closeTimeout, setCloseTimeout] = useState(null)

  const fileInputRef = useRef(null)
  const [selectedSubmission, setSelectedSubmission] = useState(null)
  const [openCorrectionDialog, setOpenCorrectionDialog] = useState(false)
  const [correctionMessage, setCorrectionMessage] = useState("")
  const [correctionFiles, setCorrectionFiles] = useState([])
  const [correctionStatus, setCorrectionStatus] = useState(DialogStatus.Default)
  const [openCorrectionDetails, setOpenCorrectionDetails] = useState(false)
  const [selectedCorrection, setSelectedCorrection] = useState(null)
  const [downloadingSubmissionUserIds, setDownloadingSubmissionUserIds] = useState([])
  const [zipInlineBanners, setZipInlineBanners] = useState([])

  const getExercise = useCallback(async () => {
    setLoading(true)
    try {
      const exercise = await api.get(`/teacher/exercises/${exerciseId}`)
      setExercise(exercise)
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }, [exerciseId])

  useEffect(() => {
    getExercise()
  }, [getExercise])

  const applySubmissionFilters = useCallback((filterIndex, searchText, sourceExercises) => {
    const source = sourceExercises ?? usersExercises
    const normalizedSearch = (searchText ?? "").toLowerCase().trim()

    let filtered = source
    if (filterIndex === 1) {
      filtered = filtered.filter((e) => e.correction || (e.corrections && e.corrections.length > 0))
    } else if (filterIndex === 2) {
      filtered = filtered.filter((e) => !e.correction && (!e.corrections || e.corrections.length === 0))
    }

    if (normalizedSearch) {
      filtered = filtered.filter((e) => {
        const fullName = `${e.user?.name ?? ""} ${e.user?.surname ?? ""}`.toLowerCase().trim()
        return fullName.includes(normalizedSearch)
      })
    }

    setFilteredUsersExercises(filtered)
  }, [usersExercises])

  useEffect(() => {
    if (!exercise) {
      return
    }

    const { users_exercises } = exercise
    setUsersExercises(users_exercises)
    applySubmissionFilters(submissionsFilter, studentSearchQuery, users_exercises)

  }, [exercise, applySubmissionFilters, submissionsFilter, studentSearchQuery])

  const onSave = useCallback(() => {
    getExercise()
    setDialogTimeout(setTimeout(() => setOpenEditDialog(false), 4000))
  }, [getExercise])

  const handleFilterChange = useCallback((index) => {
    setSubmissionsFilter(index)
    applySubmissionFilters(index, studentSearchQuery)
  }, [applySubmissionFilters, studentSearchQuery])

  const handleStudentSearch = useCallback((text) => {
    const query = text ?? ""
    setStudentSearchQuery(query)
    applySubmissionFilters(submissionsFilter, query)
  }, [applySubmissionFilters, submissionsFilter])

  const isCorrectionAvailable = useCallback(() => {
    if (!exercise?.expires_at) {
      return false
    }
    return new Date(exercise.expires_at) < new Date()
  }, [exercise])

  const formatBytes = useCallback((bytes) => {
    const value = Number(bytes)
    if (!Number.isFinite(value) || value <= 0) {
      return "0 B"
    }

    const units = ["B", "KB", "MB", "GB", "TB"]
    const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
    const scaled = value / (1024 ** index)
    const decimals = scaled >= 100 || index === 0 ? 0 : 1
    return `${scaled.toFixed(decimals)} ${units[index]}`
  }, [])

  const onDownloadSubmission = useCallback(async (submission, existingBannerId = null) => {
    if (!submission?.medias?.length) {
      return
    }

    const submissionUserId = submission?.user?.id
    if (!exercise?.id || !submissionUserId || downloadingSubmissionUserIds.includes(submissionUserId)) {
      return
    }

    const bannerId = existingBannerId ?? `${submissionUserId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const studentName = `${submission?.user?.name ?? ""} ${submission?.user?.surname ?? ""}`.trim()
    const totalBytes = (submission?.medias ?? []).reduce((sum, media) => {
      const mediaSize = Number(media?.size)
      return sum + (Number.isFinite(mediaSize) && mediaSize > 0 ? mediaSize : 0)
    }, 0)
    const totalSizeLabel = formatBytes(totalBytes)

    try {
      setDownloadingSubmissionUserIds((current) => [...current, submissionUserId])
      if (existingBannerId) {
        setZipInlineBanners((current) => current.map((banner) => (
          banner.id === bannerId
            ? {
              ...banner,
              type: "info",
              message: `Generazione ZIP in corso${studentName ? ` per ${studentName}` : ""}. Il download partirà automaticamente appena pronto. Non chiudere questa finestra.`,
              totalSize: totalSizeLabel,
            }
            : banner
        )))
      } else {
        setZipInlineBanners((current) => [...current, {
          id: bannerId,
          type: "info",
          message: `Generazione ZIP in corso${studentName ? ` per ${studentName}` : ""}. Il download partirà automaticamente appena pronto. Non chiudere questa finestra.`,
          totalSize: totalSizeLabel,
          submission,
          submissionUserId,
        }])
      }

      const zipBlob = await api.get(`/teacher/exercises/${exercise.id}/users/${submissionUserId}/submission.zip`, {
        responseType: "blob",
        timeout: 0
      })

      const safeName = `${submission?.user?.name ?? ""}-${submission?.user?.surname ?? ""}`
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-zA-Z0-9-_]/g, "") || "student"

      const downloadFileName = `exercise-${exercise.id}-submission-${safeName}.zip`
      const blobUrl = window.URL.createObjectURL(zipBlob)

      const link = document.createElement("a")
      link.href = blobUrl
      link.download = downloadFileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)

      // Remove the banner only once the download trigger has been fired.
      setZipInlineBanners((current) => current.filter((banner) => banner.id !== bannerId))
    } catch (error) {
      console.error("Unable to download submission zip", error)
      setZipInlineBanners((current) => current.map((banner) => (
        banner.id === bannerId
          ? {
            ...banner,
            type: "error",
            message: "Non siamo riusciti a generare lo ZIP della consegna. Riprova tra poco."
          }
          : banner
      )))
    } finally {
      setDownloadingSubmissionUserIds((current) => current.filter((id) => id !== submissionUserId))
    }
  }, [downloadingSubmissionUserIds, exercise?.id, formatBytes])

  const openCorrectionForSubmission = useCallback((submission) => {
    setSelectedSubmission(submission)

    if (submission?.correction) {
      setSelectedCorrection(submission.correction)
      setOpenCorrectionDetails(true)
      return
    }

    if (!isCorrectionAvailable()) {
      return
    }

    setSelectedSubmission(submission)
    setOpenCorrectionDialog(true)
  }, [isCorrectionAvailable])

  const sendCorrection = useCallback(async () => {
    if (!selectedSubmission || !exercise?.id) {
      return
    }

    try {
      setCorrectionStatus(DialogStatus.Loading)
      const form = new FormData()
      form.append("message", correctionMessage)
      form.append("user_id", selectedSubmission.user.id)
      for (let i = 0; i < correctionFiles.length; i++) {
        form.append("files", correctionFiles[i])
      }

      await api.post(`/teacher/exercises/${exercise.id}`, form, {
        headers: { "Content-Type": "multipart/form-data" }
      })

      setCorrectionStatus(DialogStatus.Success)
      await getExercise()
      setTimeout(() => {
        setOpenCorrectionDialog(false)
        setCorrectionStatus(DialogStatus.Default)
        setCorrectionMessage("")
        setCorrectionFiles([])
        setSelectedSubmission(null)
      }, 800)
    } catch (e) {
      console.error(e)
      setCorrectionStatus(DialogStatus.Error)
    }
  }, [selectedSubmission, exercise, correctionMessage, correctionFiles, getExercise])

  const getSubmissionActions = useCallback((submission) => {
    const correctionBlocked = !isCorrectionAvailable() && !submission?.correction
    const filesCount = Array.isArray(submission?.medias) ? submission.medias.length : 0
    const isZipGenerating = downloadingSubmissionUserIds.includes(submission?.user?.id)
    const downloadLabel = isZipGenerating
      ? "Generazione ZIP..."
      : filesCount === 1 ? "Scarica 1 file" : `Scarica ${filesCount} file`

    return [
      {
        label: downloadLabel,
        onClick: () => onDownloadSubmission(submission),
        disabled: isZipGenerating,
      },
      {
        label: submission?.correction ? t("exercises.corrections") : t("exercises.correctAction"),
        onClick: () => openCorrectionForSubmission(submission),
        disabled: correctionBlocked,
      }
    ]
  }, [downloadingSubmissionUserIds, isCorrectionAvailable, onDownloadSubmission, openCorrectionForSubmission, t])

  const deleteExercise = useCallback(async () => {
    try {
      setAlertStatus(DialogStatus.Loading)
      await api.delete(`/teacher/exercises/${exercise.id}`)
      setAlertStatus(DialogStatus.Success)
      setCloseTimeout(setTimeout(() => {
        setOpenAlert(false)
        setAlertStatus(DialogStatus.Default)
        navigate("/exercises")
      }, 1000))
    } catch (e) {
      console.error(e)
      setAlertStatus(DialogStatus.Error)

      setCloseTimeout(setTimeout(() => {
        setOpenAlert(false)
        setAlertStatus(DialogStatus.Default)
      }, 1000))
    }
  }, [exercise, navigate])


  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>{exercise?.name ?? t("exercises.title")}</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Back onClick={() => { navigate(-1) }} />
            <div className={styles.header}>
              <div className={styles.title}>{exercise?.name ?? t("exercises.title")}</div>
              <div className={styles.actions}>
                <Button
                  appearance="subtle"
                  accentColor={"var(--sf-red)"}
                  onClick={() => setOpenAlert(true)}
                >
                  {t("delete").toUpperCase()}
                </Button>
                <Button
                  appearance="subtle"
                  accentColor={"var(--primary)"}
                  onClick={() => setOpenEditDialog(true)}
                >
                  {t("edit").toUpperCase()}
                </Button>
              </div>
            </div>
            <Card style={{ padding: 0 }}>
              {
                loading === true &&
                <>
                  <div className={styles.cardContent}>
                    <div className={styles.bottomCard}>
                      <div className={`${styles.labeled} ${styles.descriptionField}`}>
                        <Skeleton type="rect" width="96px" height="16px" borderRadius="8px" />
                        <Skeleton type="rect" width="70%" height="16px" borderRadius="8px" />
                        <Skeleton type="rect" width="48%" height="16px" borderRadius="8px" />
                      </div>
                      {
                        exercise?.expires_at &&
                        <div className={styles.labeled}>
                          <Skeleton type="rect" width="96px" height="16px" borderRadius="8px" />
                          <Skeleton type="rect" width="196px" height="16px" borderRadius="8px" />
                        </div>
                      }
                      <div className={styles.labeled}>
                        <Skeleton type="rect" width="196px" height="16px" borderRadius="8px" />
                        <Skeleton type="rect" width="96px" height="16px" borderRadius="8px" />
                      </div>
                    </div>
                  </div>
                </>
              }

              {
                loading === false &&
                <>
                  <div className={styles.cardContent}>
                    <div className={styles.bottomCard}>
                      <div className={`${styles.labeled} ${styles.descriptionField}`}>
                        <div className={styles.label}>Descrizione</div>
                        <div className={`${styles.value} ${styles.descriptionValue}`}>
                          {
                            exercise?.description
                              ? <Markdown>{exercise.description}</Markdown>
                              : <span className={styles.emptyDescription}>Nessuna descrizione disponibile.</span>
                          }
                        </div>
                      </div>
                      {
                        exercise?.expires_at &&
                        <div className={styles.labeled}>
                          <div className={styles.label}>{t("exercises.expiresAt")}</div>
                          <div className={styles.value}><CalendarIcon /> {formatDateV2(exercise?.expires_at)} {formatTimeV2(exercise?.expires_at)} </div>
                        </div>
                      }
                      <div className={styles.labeled}>
                        <div className={styles.label}>{t("exercises.submitNumber")}</div>
                        <div className={styles.value}><StudentsIcon />{exercise?.users_exercises.length} {t("outOf")} {exercise?.total_users}</div>
                      </div>
                      <div className={styles.labeled}>
                        <div className={styles.label}>{t("exercises.lesson.label")}</div>
                        <div className={styles.value}><LessonIcon />{exercise?.lesson?.name ?? t("exercises.lesson.empty")}</div>
                      </div>
                      <div className={styles.labeled}>
                        <div className={styles.label}>{t("exercises.uploadedMaterials")}</div>
                        <div className={styles.value}><MaterialIcon />{exercise?.medias?.length ?? 0}</div>
                      </div>
                    </div>
                    {
                      exercise?.medias && exercise.medias.length > 0 &&
                      <div className={styles.labeled}>
                        <div className={styles.label}>{t("exercises.materials")}</div>
                        <div className={styles.studentFilesContainer}>
                          <div className={styles.studentFiles}>
                            <Each
                              of={exercise.medias}
                              render={(file) => (
                                <div className={styles.studentFile}>
                                  <MaterialButton
                                    material={file}
                                    downloadable={true}
                                  />
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                </>
              }
            </Card>

            <Card>
              <div style={{ width: '100%', display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ width: '280px', minWidth: '280px' }}>
                  <Select
                    selected={[
                      { id: 0, name: "Tutti", value: 0 },
                      { id: 1, name: t("exercises.correctExercises"), value: 1 },
                      { id: 2, name: t("exercises.toBeCorrectedExercises"), value: 2 },
                    ].find((option) => option.value === submissionsFilter)}
                    options={[
                      { id: 0, name: "Tutti", value: 0 },
                      { id: 1, name: t("exercises.correctExercises"), value: 1 },
                      { id: 2, name: t("exercises.toBeCorrectedExercises"), value: 2 },
                    ]}
                    placeholder={"Tutti"}
                    onSelect={(option) => {
                      const nextFilter = option?.value ?? 0
                      handleFilterChange(nextFilter)
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <TextInput
                    onKeyUp={handleStudentSearch}
                    type="search"
                    placeholder="Cerca Studente"
                    style={{ backgroundColor: "var(--background-secondary-color)" }}
                  />
                </div>
              </div>

              {zipInlineBanners.map((banner) => (
                <div key={banner.id} className={`${styles.zipInlineBanner} ${banner.type === "error" ? styles.zipInlineBannerError : ""}`}>
                  <div className={styles.zipInlineBannerInfo}>
                    {banner.type === "info" && <span className={styles.zipInlineSpinner} />}
                    <div className={styles.zipInlineBannerText}>
                      <span>{banner.message}</span>
                      {banner.type === "info" && banner.totalSize &&
                        <span className={styles.zipInlineBannerMeta}>Dimensione totale stimata: {banner.totalSize}</span>
                      }
                    </div>
                  </div>
                  {banner.type === "error" &&
                    <div className={styles.zipInlineBannerActions}>
                      <Button
                        appearance="subtle"
                        onClick={() => onDownloadSubmission(banner.submission, banner.id)}
                        disabled={downloadingSubmissionUserIds.includes(banner.submissionUserId)}
                      >
                        RIPROVA
                      </Button>
                      <button
                        type="button"
                        className={styles.zipInlineBannerClose}
                        onClick={() => setZipInlineBanners((current) => current.filter((item) => item.id !== banner.id))}
                      >
                        Chiudi
                      </button>
                    </div>
                  }
                </div>
              ))}

              {
                loading === true &&
                <Table
                  appearance="minimal"
                  loading
                  columns={[
                    { key: 'user_name', label: 'STUDENTE', type: 'string' },
                    { key: 'created_at', label: 'CONSEGNATO IL', type: 'datetime' },
                    { key: 'materials_count', label: 'FILE', type: 'number' },
                    { key: '_status', label: 'STATO', type: 'custom' },
                    { key: '_actions', label: 'AZIONI', type: 'custom' },
                  ]}
                  elements={[]}
                />
              }

              {
                !loading &&
                <Table
                appearance="minimal"
                  columns={[
                    { key: 'user_name', label: 'STUDENTE', type: 'string' },
                    { key: 'created_at', label: 'CONSEGNATO IL', type: 'datetime' },
                    { key: 'materials_count', label: 'FILE', type: 'number' },
                    { key: '_status', label: 'STATO', type: 'custom' },
                    { key: '_actions', label: 'AZIONI', type: 'custom' },
                  ]}
                  elements={filteredUsersExercises.map((ue) => ({
                    ...ue,
                    user_name: `${ue.user?.name ?? ''} ${ue.user?.surname ?? ''}`.trim(),
                    materials_count: ue.medias?.length ?? 0,
                    _status: {
                      value: ue.correction ? 'Corretta' : 'Da correggere',
                      type: 'string',
                      component: (
                        <div className={ue.correction ? styles.correctedBadge : styles.pendingBadge}>
                          {ue.correction ? "CORRETTO" : t("exercises.toBeCorrectedExercises")}
                        </div>
                      )
                    },
                    _actions: {
                      value: ue.user?.id,
                      type: 'number',
                      component: <MeatBallsMenu actions={getSubmissionActions(ue)} />
                    }
                  }))}
                />
              }
            </Card>

          </div>
        </div>
        <ExerciseDialog
          open={openEditDialog}
          onClose={() => {
            if (dialogTimeout) {
              clearTimeout(dialogTimeout)
              setDialogTimeout(null)
            }
            setOpenEditDialog(false)
          }}
          exercise={exercise}
          status={dialogStatus}
          onSave={onSave}
        />

        <Dialog
          open={openCorrectionDialog}
          title={`Correzione per ${selectedSubmission?.user?.name ?? ''} ${selectedSubmission?.user?.surname ?? ''}`.trim()}
          style={{ maxWidth: "90vw" }}
          status={correctionStatus}
          onClose={() => {
            setOpenCorrectionDialog(false)
            setSelectedSubmission(null)
            setCorrectionMessage("")
            setCorrectionFiles([])
            setCorrectionStatus(DialogStatus.Default)
          }}
          action={
            <Button onClick={sendCorrection} disabled={correctionStatus !== DialogStatus.Default}>
              {t("send").toUpperCase()}
            </Button>
          }
        >
          <div className={`${styles.correctionDialogModern} ${styles.fullWidthDialogContent}`}>
            <div className={styles.correctionTextAreaWrap}>
              <textarea
                className={styles.correctionTextArea}
                rows={5}
                value={correctionMessage}
                placeholder={t("exercises.correctionText")}
                onChange={(e) => setCorrectionMessage(e.target.value)}
              />
            </div>

            <div className={styles.correctionUploadSection}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? [])
                  setCorrectionFiles(files)
                }}
              />
              <div className={styles.correctionDropzone}>
                <div className={styles.correctionDropzoneLabel}>{"Aggiungi file correzione"}</div>
                <Button
                  inverse
                  accentColor={"var(--primary)"}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {"CARICA FILE"}
                </Button>
              </div>

              {correctionFiles.length > 0 &&
                <div className={styles.correctionFilesList}>
                  <Each
                    of={correctionFiles}
                    render={(file) => (
                      <div className={styles.correctionFileItem}>{file.name}</div>
                    )}
                  />
                </div>
              }
            </div>
          </div>
        </Dialog>

        <Dialog
          open={openCorrectionDetails}
          title={`Correzione per ${selectedSubmission?.user?.name ?? ''} ${selectedSubmission?.user?.surname ?? ''}`.trim()}
          onClose={() => {
            setOpenCorrectionDetails(false)
            setSelectedCorrection(null)
          }}
        >
          <div className={`${styles.correctionDialogReadOnly} ${styles.fullWidthDialogContent}`}>
            <div className={styles.correctionReadOnlyMessage}><Markdown>{selectedCorrection?.message ?? ""}</Markdown></div>
            <div className={styles.correctionReadOnlyMaterials}>
              <Each
                of={selectedCorrection?.medias ?? []}
                render={(media) => (
                  <div className={styles.correctionReadOnlyFile}>
                    <MaterialButton material={media} downloadable />
                  </div>
                )}
              />
            </div>
            {selectedCorrection?.created_at &&
              <div className={styles.correctionReadOnlyDate}>
                {formatDateV2(selectedCorrection.created_at, { day: 'numeric', month: 'long', year: 'numeric' })} - {formatTimeV2(selectedCorrection.created_at)}
              </div>
            }
          </div>
        </Dialog>

        <AlertDialog
          open={openAlert}
          title={t("exercises.deleteTitle")}
          text={t("exercises.deleteText")}
          status={alertStatus}
          onClose={() => {
            if (dialogStatus === DialogStatus.Success) {
              navigate("/exercises")
            }
            setOpenAlert(false)
            setAlertStatus(DialogStatus.Default)
            if (closeTimeout) {
              clearTimeout(closeTimeout)
            }
          }}
          actions={[
            {
              label: t("exercises.deleteConfirm").toUpperCase(),
              onClick: deleteExercise,
            }
          ]}
        />
      </div>
    </HeaderFooterLayout >
  )

}

export default Exercise
